import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBBtn,
  MDBBadge,
  MDBIcon,
  MDBTypography,
  MDBTable,
  MDBTableHead,
  MDBTableBody,
  MDBInput
} from 'mdb-react-ui-kit';
import { ShootingSession, ShotData, InventoryItem } from '../types';
import { apiService } from '../services/apiService';
import { inventoryService } from '../services/inventoryService';
import { useAuth } from '../contexts/AuthContext';
import AnnotatedTargetImage from '../components/AnnotatedTargetImage';
import './SessionDetails.css';

interface SessionDetailsProps {
  theme: 'light' | 'dark';
}

const SessionDetails: React.FC<SessionDetailsProps> = ({ theme }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [session, setSession] = useState<ShootingSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedShots, setEditedShots] = useState<ShotData[]>([]);
  const [editedTotalScore, setEditedTotalScore] = useState(0);
  const [editedNotes, setEditedNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Inventory state
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [selectedInventoryItemId, setSelectedInventoryItemId] = useState<string>('');

  useEffect(() => {
    if (!authLoading && isAuthenticated && id) {
      loadSession(id);
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [id, authLoading, isAuthenticated]);

  // Load inventory items
  useEffect(() => {
    const loadInventory = async () => {
      if (!authLoading && isAuthenticated) {
        try {
          const items = await inventoryService.getInventory();
          setInventoryItems(items);
        } catch (error) {
          console.error('[SessionDetails] Failed to load inventory:', error);
        }
      }
    };
    loadInventory();
  }, [authLoading, isAuthenticated]);

  const loadSession = async (sessionId: string) => {
    try {
      setLoading(true);
      const loadedSession = await apiService.getSession(sessionId);
      setSession(loadedSession);
    } catch (error) {
      alert('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (session: ShootingSession): string | null => {
    // Return base64 image data stored directly in session
    if (session.targetImageData) {
      return session.targetImageData;
    }
    
    return null;
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const getScoreColor = (score: number): string => {
    if (score >= 10) return 'var(--score-excellent)';
    if (score >= 9) return 'var(--score-good)';
    if (score >= 8) return 'var(--score-fair)';
    return 'var(--score-poor)';
  };

  const calculateTotalScore = (shots: ShotData[]): number => {
    return shots.reduce((sum, shot) => sum + shot.ring, 0);
  };

  const handleEditClick = () => {
    if (session) {
      setEditedShots([...session.shots]);
      setEditedTotalScore(Math.round(session.totalScore));
      setEditedNotes(session.notes || '');
      setSelectedInventoryItemId(session.inventoryItemId || '');
      setIsEditMode(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedShots([]);
    setEditedTotalScore(0);
    setEditedNotes('');
    setSelectedInventoryItemId('');
  };

  const handleSaveEdit = async () => {
    if (!session || !id) return;

    try {
      setIsSaving(true);
      const shotCount = editedShots.length;

      await apiService.updateSession(id, {
        shots: editedShots,
        totalScore: editedTotalScore,
        shotCount,
        notes: editedNotes || undefined,
        inventoryItemId: selectedInventoryItemId || undefined,
      });

      // Reload session to get updated data
      await loadSession(id);
      setIsEditMode(false);
      setEditedShots([]);
      setEditedTotalScore(0);
      setEditedNotes('');
      setSelectedInventoryItemId('');
    } catch (error) {
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleShotChange = (index: number, field: keyof ShotData, value: string | number) => {
    const updatedShots = [...editedShots];
    if (field === 'ring') {
      const ringValue = typeof value === 'string' ? parseFloat(value) : value;
      if (ringValue >= 0 && ringValue <= 10) {
        updatedShots[index] = { ...updatedShots[index], ring: ringValue };
        setEditedShots(updatedShots);
      }
    } else if (field === 'x' || field === 'y') {
      const numValue = typeof value === 'string' ? parseInt(value) : value;
      updatedShots[index] = { ...updatedShots[index], [field]: numValue };
      setEditedShots(updatedShots);
    }
  };

  const handleAddShot = () => {
    const newShot: ShotData = {
      shot: editedShots.length + 1,
      ring: 0,
      x: 0,
      y: 0,
    };
    setEditedShots([...editedShots, newShot]);
  };

  const handleDeleteShot = (index: number) => {
    const updatedShots = editedShots.filter((_, i) => i !== index);
    // Renumber shots
    const renumberedShots = updatedShots.map((shot, i) => ({
      ...shot,
      shot: i + 1,
    }));
    setEditedShots(renumberedShots);
  };

  if (loading) {
    return (
      <MDBContainer className="py-4">
        <MDBCard>
          <MDBCardBody className="text-center py-5">
            <MDBIcon fas icon="spinner" spin size="2x" className="text-primary" />
            <MDBTypography className="mt-3">Loading session...</MDBTypography>
          </MDBCardBody>
        </MDBCard>
      </MDBContainer>
    );
  }

  if (!session) {
    return (
      <MDBContainer className="py-4">
        <MDBCard>
          <MDBCardBody className="text-center py-5">
            <MDBIcon fas icon="exclamation-circle" size="3x" className="text-danger mb-3" />
            <MDBTypography tag="h5">Session not found</MDBTypography>
            <MDBBtn color="primary" className="mt-3" onClick={() => navigate('/sessions')}>
              <MDBIcon fas icon="arrow-left" className="me-2" />
              Back to Sessions
            </MDBBtn>
          </MDBCardBody>
        </MDBCard>
      </MDBContainer>
    );
  }

  return (
    <MDBContainer className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <MDBBtn color="light" onClick={() => navigate('/sessions')}>
          <MDBIcon fas icon="arrow-left" className="me-2" />
          Back to Sessions
        </MDBBtn>
        <MDBTypography tag="h1" className="mb-0">Session Details</MDBTypography>
        {!isEditMode && (
          <MDBBtn color="primary" onClick={handleEditClick}>
            <MDBIcon fas icon="edit" className="me-2" />
            Edit Session
          </MDBBtn>
        )}
      </div>

      {/* Session Info Card */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBRow>
            <MDBCol md="6">
              <MDBTypography className="text-muted small mb-1">Date</MDBTypography>
              <MDBTypography>{formatDate(session.date)}</MDBTypography>
            </MDBCol>
            {session.sessionType && (
              <MDBCol md="6">
                <MDBTypography className="text-muted small mb-1">Type</MDBTypography>
                <div>
                  <MDBBadge
                    color={session.sessionType === 'automatic' ? 'success' : 'info'}
                    className="p-2"
                  >
                    <MDBIcon fas icon={session.sessionType === 'automatic' ? 'bullseye' : 'camera'} className="me-1" />
                    {session.sessionType === 'automatic' ? 'Automatic' : 'Manual'}
                  </MDBBadge>
                </div>
              </MDBCol>
            )}
            {session.location?.address && (
              <MDBCol md="6">
                <MDBTypography className="text-muted small mb-1">Location</MDBTypography>
                <MDBTypography>
                  <MDBIcon fas icon="map-marker-alt" className="me-2" />
                  {session.location.address}
                </MDBTypography>
              </MDBCol>
            )}
          </MDBRow>
        </MDBCardBody>
      </MDBCard>
      {/* Stats Summary */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBRow className="text-center">
            <MDBCol md="6" className="border-end">
              <MDBTypography className="text-muted small mb-2">Total Shots</MDBTypography>
              <MDBTypography tag="h3" className="text-primary">
                {isEditMode ? editedShots.length : session.shotCount}
              </MDBTypography>
            </MDBCol>
            <MDBCol md="6">
              <MDBTypography className="text-muted small mb-2">Total Score</MDBTypography>
              {isEditMode ? (
                <MDBInput
                  type="number"
                  value={editedTotalScore}
                  onChange={(e) => {
                    const value = Math.round(parseFloat(e.target.value) || 0);
                    setEditedTotalScore(value);
                  }}
                  min="0"
                  step="1"
                  className="text-center fw-bold"
                  style={{ fontSize: '1.75rem' }}
                />
              ) : (
                <MDBTypography tag="h3" className="text-primary">
                  {Math.round(session.totalScore)}
                </MDBTypography>
              )}
            </MDBCol>
          </MDBRow>
        </MDBCardBody>
      </MDBCard>

      {/* Target Photo (if available for manual sessions) */}
      {(session.targetImageUri || session.targetImageData) && (() => {
        const imageUrl = getImageUrl(session);
        return imageUrl ? (
          <MDBCard className="mb-4">
            <MDBCardBody>
              <MDBTypography tag="h5" className="mb-3">Target Photo with Detected Shots</MDBTypography>
              {imageError ? (
                <div className="text-center py-5" style={{ 
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 152, 0, 0.3)'
                }}>
                  <MDBIcon fas icon="camera" size="3x" className="text-warning mb-3" />
                  <MDBTypography tag="h6">Image not yet synced to server</MDBTypography>
                  <MDBTypography className="text-muted small">
                    The image may still be uploading from the mobile device.<br />
                    Please ensure the mobile device has synced and try refreshing the page.
                  </MDBTypography>
                </div>
              ) : (
                <div className="text-center">
                  <AnnotatedTargetImage 
                    imageUri={imageUrl}
                    width={500}
                    height={500}
                    onError={() => setImageError(true)}
                  />
                </div>
              )}
            </MDBCardBody>
          </MDBCard>
        ) : (
          <MDBCard className="mb-4">
            <MDBCardBody className="text-center py-5">
              <MDBIcon fas icon="image" size="3x" className="text-muted mb-3" />
              <MDBTypography className="text-muted">Image not available on server</MDBTypography>
            </MDBCardBody>
          </MDBCard>
        );
      })()}

      {/* Shot Details Table */}
      <MDBCard>
        <MDBCardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <MDBTypography tag="h5" className="mb-0">Shot Details</MDBTypography>
            {isEditMode && (
              <MDBBtn color="success" size="sm" onClick={handleAddShot}>
                <MDBIcon fas icon="plus" className="me-2" />
                Add Shot
              </MDBBtn>
            )}
          </div>
          <div className="table-responsive">
            <MDBTable striped hover>
              <MDBTableHead>
                <tr>
                  <th>Shot</th>
                  <th>Ring</th>
                  {session.shots[0]?.timestamp && <th>Time</th>}
                  {isEditMode && <th>Actions</th>}
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {(isEditMode ? editedShots : session.shots).map((shot, index) => (
                  <tr key={shot.shot}>
                    <td>{shot.shot}</td>
                    <td>
                      {isEditMode ? (
                        <MDBInput
                          type="number"
                          value={shot.ring}
                          onChange={(e) => handleShotChange(index, 'ring', e.target.value)}
                          min="0"
                          max="10"
                          step="0.1"
                          size="sm"
                          style={{ width: '100px' }}
                        />
                      ) : (
                        shot.ring
                      )}
                    </td>
                    {shot.timestamp && (
                      <td>{new Date(shot.timestamp).toLocaleTimeString()}</td>
                    )}
                    {isEditMode && (
                      <td>
                        <MDBBtn
                          color="danger"
                          size="sm"
                          floating
                          onClick={() => handleDeleteShot(index)}
                          title="Delete shot"
                        >
                          <MDBIcon fas icon="trash" />
                        </MDBBtn>
                      </td>
                    )}
                  </tr>
                ))}
              </MDBTableBody>
            </MDBTable>
          </div>
          {isEditMode && (
            <div className="d-flex justify-content-end gap-2 mt-3">
              <MDBBtn color="secondary" onClick={handleCancelEdit} disabled={isSaving}>
                Cancel
              </MDBBtn>
              <MDBBtn color="primary" onClick={handleSaveEdit} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <MDBIcon fas icon="spinner" spin className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <MDBIcon fas icon="save" className="me-2" />
                    Save Changes
                  </>
                )}
              </MDBBtn>
            </div>
          )}
        </MDBCardBody>
      </MDBCard>

      {/* Weapon Card */}
      <MDBCard className="mt-4">
        <MDBCardBody>
          <MDBTypography tag="h5" className="mb-3">
            <MDBIcon fas icon="crosshairs" className="me-2" />
            Weapon
          </MDBTypography>
          {isEditMode ? (
            <>
              <select
                className="form-select"
                value={selectedInventoryItemId}
                onChange={(e) => setSelectedInventoryItemId(e.target.value)}
              >
                <option value="">No weapon selected</option>
                {inventoryItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} {item.caliber ? `(${item.caliber})` : ''}
                  </option>
                ))}
              </select>
              {inventoryItems.length === 0 && (
                <MDBTypography className="text-muted small mt-2">
                  No weapons in inventory. Add weapons in Settings → Inventory.
                </MDBTypography>
              )}
            </>
          ) : (
            <MDBTypography className={session.inventoryItemId ? '' : 'text-muted fst-italic'}>
              {session.inventoryItemId
                ? inventoryItems.find(item => item.id === session.inventoryItemId)?.name || 'Unknown weapon'
                : 'No weapon selected'}
            </MDBTypography>
          )}
        </MDBCardBody>
      </MDBCard>

      {/* Location Card */}
      <MDBCard className="mt-4">
        <MDBCardBody>
          <MDBTypography tag="h5" className="mb-3">
            <MDBIcon fas icon="map-marker-alt" className="me-2" />
            Location
          </MDBTypography>
          {session.location ? (
            <div>
              <MDBTypography className="mb-2">
                <MDBIcon fas icon="map-pin" className="me-2 text-primary" />
                {session.location.address || `${session.location.latitude.toFixed(6)}, ${session.location.longitude.toFixed(6)}`}
              </MDBTypography>
              <a
                href={`https://www.google.com/maps?q=${session.location.latitude},${session.location.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
              >
                <MDBIcon fas icon="external-link-alt" className="me-2" />
                View on Map
              </a>
            </div>
          ) : (
            <MDBTypography className="text-muted fst-italic">
              No location recorded
            </MDBTypography>
          )}
        </MDBCardBody>
      </MDBCard>

      {/* Notes Card */}
      <MDBCard className="mt-4">
        <MDBCardBody>
          <MDBTypography tag="h5" className="mb-3">
            <MDBIcon fas icon="sticky-note" className="me-2" />
            Notes
          </MDBTypography>
          {isEditMode ? (
            <MDBInput
              textarea
              rows={4}
              value={editedNotes}
              onChange={(e) => setEditedNotes(e.target.value)}
              placeholder="Add notes about this session..."
            />
          ) : (
            <MDBTypography className={session.notes ? '' : 'text-muted fst-italic'}>
              {session.notes || 'No notes added'}
            </MDBTypography>
          )}
        </MDBCardBody>
      </MDBCard>
    </MDBContainer>
  );
};

export default SessionDetails;
